import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Typography,
  CssBaseline,
  Snackbar,
} from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import CustomTextField from '../../main/widgets/CustomTextField';
import LocationOptionList from '../../main/widgets/LocationOptionList';
import StatusOptionList from '../../main/widgets/StatusOptionList';
import TrackingEventTypeOptionList from '../../main/widgets/TrackingEventTypeOptionList';
import { theme } from '../../main/theme/constants';
import { ThemeProvider } from '@mui/joy';
import {
  getTrackingEvent,
  createTrackingEvent,
  updateTrackingEvent,
} from '../../main/API/trackingEvent';
import { toDatetimeLocalValue, datetimeLocalToIso, buildTimestampUpdateField } from '../../main/utils/trackingEventLabels';
import { useTranslation } from 'react-i18next';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';

function getDefaultTimestamp() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function TrackingEventForm({ eventId, formType }) {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);
  const isEdit = formType === 'edit' && eventId;

  const [shipment, setShipment] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventTypeOpt, setEventTypeOpt] = useState(null);
  const [statusSnapshot, setStatusSnapshot] = useState('');
  const [location, setLocation] = useState(null);
  const [timestamp, setTimestamp] = useState(getDefaultTimestamp());
  const [description, setDescription] = useState('');
  const [updateShipmentStatus, setUpdateShipmentStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [currentLocationText, setCurrentLocationText] = useState('');
  const [addCurrentLocationRecord, setAddCurrentLocationRecord] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [createdBy, setCreatedBy] = useState(null);
  const [originalTimestamp, setOriginalTimestamp] = useState(null);

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successSnack, setSuccessSnack] = useState(false);
  const [failedSnack, setFailedSnack] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const defaultTimestamp = useMemo(() => getDefaultTimestamp(), []);

  useEffect(() => {
    if (isEdit && eventId) {
      if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_VIEW)) return;
      setLoading(true);
      getTrackingEvent(eventId)
        .then((res) => {
          const e = res.data;
          setShipment(String(e.shipment ?? ''));
          setEventType(e.event_type ?? '');
          setEventTypeOpt(
            e.event_type ? { code: e.event_type, name: e.event_type } : null,
          );
          setStatusSnapshot(e.status_snapshot ?? '');
          setLocation(e.location != null ? (typeof e.location === 'object' ? e.location : { id: e.location }) : null);
          setOriginalTimestamp(e.timestamp || null);
          setTimestamp(toDatetimeLocalValue(e.timestamp) || defaultTimestamp);
          setDescription(e.description ?? '');
          if (e.created_by != null) setCreatedBy(e.created_by);
        })
        .catch((err) => {
          setErrorMessage(translateAxiosErrorMessage(err, t) || t('trackingEvent.loadEventError'));
          setFailedSnack(true);
        })
        .finally(() => setLoading(false));
    } else {
      setTimestamp(defaultTimestamp);
    }
  }, [eventId, isEdit, defaultTimestamp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shipment || !eventType) {
      setErrorMessage(t('trackingEvent.errShipmentAndEventRequired'));
      setFailedSnack(true);
      return;
    }
    if (isEdit) {
      if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_UPDATE)) return;
    } else {
      if (!hasPermission(PERMISSIONS.TRACKING_EVENTS_CREATE)) return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        shipment: parseInt(shipment, 10),
        event_type: eventType,
        status_snapshot: statusSnapshot || undefined,
        location: location?.id ?? location ?? undefined,
        description: description || undefined,
      };
      if (isEdit) {
        const ts = buildTimestampUpdateField(originalTimestamp, timestamp);
        if (ts) payload.timestamp = ts;
      } else {
        const ts = datetimeLocalToIso(timestamp);
        if (ts) payload.timestamp = ts;
      }
      if (isEdit) {
        await updateTrackingEvent(eventId, payload, true);
        setSuccessSnack(true);
        setTimeout(() => navigate('/shipment/tracking-events'), 1500);
      } else {
        payload.update_shipment_status = updateShipmentStatus;
        if (updateShipmentStatus && newStatus) payload.new_status = newStatus;
        if (updateShipmentStatus && currentLocationText) payload.current_location_text = currentLocationText;
        payload.add_current_location_record = addCurrentLocationRecord;
        payload.send_notification = sendNotification;
        const res = await createTrackingEvent(payload);
        setSuccessSnack(true);
        const id = res.data && res.data.id;
        if (id) setTimeout(() => navigate(`/shipment/tracking-events/${id}/edit`), 1500);
        else setTimeout(() => navigate('/shipment/tracking-events'), 1500);
      }
    } catch (err) {
      setErrorMessage(translateAxiosErrorMessage(err, t) || t('trackingEvent.requestFailed'));
      setFailedSnack(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ p: 3 }}>
          <Typography>{t('trackingEvent.formLoading')}</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', width: '100%' }} m={3}>
        <Box width="100%" component="form" onSubmit={handleSubmit}>
          <Typography level="h4" sx={{ mb: 2 }}>
            {isEdit ? t('trackingEvent.formTitleEdit') : t('trackingEvent.formTitleAdd')}
          </Typography>

          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={4}>
              <CustomTextField
                type="number"
                label={t('trackingEvent.shipmentIdLabel')}
                required
                value={shipment}
                onChange={(e) => setShipment(e.target.value)}
                disabled={isEdit}
              />
            </Grid>
            {canListEventTypes && (
              <Grid xs={12} sm={6} md={4}>
                <TrackingEventTypeOptionList
                  label={t('trackingEvent.eventTypeLabelForm')}
                  required
                  defaultValue={eventTypeOpt}
                  onChange={(_, v) => {
                    setEventTypeOpt(v ?? null);
                    setEventType((v && v.code) || '');
                  }}
                />
              </Grid>
            )}
            <Grid xs={12} sm={6} md={4}>
              <StatusOptionList
                label={t('trackingEvent.statusSnapshot')}
                status={statusSnapshot || ''}
                setStatus={(v) => setStatusSnapshot(v ?? '')}
              />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <LocationOptionList
                defaultValue={location}
                onChange={(_, value) => setLocation(value)}
                required={false}
                label={t('widgets.location')}
              />
            </Grid>
            <Grid xs={12} sm={6} md={4}>
              <CustomTextField
                type="datetime-local"
                label={t('trackingEvent.timestamp')}
                value={timestamp}
                onChange={(e) => setTimestamp(e.target.value)}
              />
            </Grid>
            <Grid xs={12}>
              <FormControl>
                <FormLabel>{t('trackingEvent.descriptionMaxChars')}</FormLabel>
                <Textarea
                  minRows={2}
                  maxRows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                  placeholder={t('trackingEvent.descriptionOptionalPlaceholder')}
                />
                <Typography level="body3" sx={{ mt: 0.5 }}>
                  {description.length}/500
                </Typography>
              </FormControl>
            </Grid>

            {!isEdit && (
              <>
                <Grid xs={12}>
                  <Typography level="h5" sx={{ mt: 1, mb: 1 }}>
                    {t('trackingEvent.createOptionsTitle')}
                  </Typography>
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                  <NativeCheckbox
                    checked={updateShipmentStatus}
                    onChange={(e) => setUpdateShipmentStatus(e.target.checked)}
                    label="Update shipment status"
                  />
                </Grid>
                {updateShipmentStatus && (
                  <>
                    <Grid xs={12} sm={6} md={4}>
                      <StatusOptionList
                        label={t('trackingEvent.newStatus')}
                        status={newStatus || ''}
                        setStatus={(v) => setNewStatus(v ?? '')}
                      />
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <CustomTextField
                        type="text"
                        label={t('trackingEvent.currentLocationText')}
                        value={currentLocationText}
                        onChange={(e) => setCurrentLocationText(e.target.value)}
                      />
                    </Grid>
                  </>
                )}
                <PermissionGate permission={PERMISSIONS.CURRENT_LOCATION_MANAGE}>
                  <Grid xs={12} sm={6} md={3}>
                    <NativeCheckbox
                      checked={addCurrentLocationRecord}
                      onChange={(e) => setAddCurrentLocationRecord(e.target.checked)}
                      label={t('trackingEvent.addCurrentLocationRecord')}
                    />
                  </Grid>
                </PermissionGate>
                <Grid xs={12} sm={6} md={3}>
                  <NativeCheckbox
                    checked={sendNotification}
                    onChange={(e) => setSendNotification(e.target.checked)}
                    label={t('trackingEvent.sendWhatsAppNotification')}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button type="submit" loading={submitLoading}>
              {isEdit ? t('shipmentForm.update') : t('trackingEvent.createButton')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="neutral"
              onClick={() => navigate('/shipment/tracking-events')}
            >
              {t('actions.cancel')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={successSnack}
        color="success"
        autoHideDuration={3000}
        onClose={() => setSuccessSnack(false)}
      >
        {isEdit ? t('trackingEvent.snackbarUpdated') : t('trackingEvent.snackbarCreated')}
      </Snackbar>
      <Snackbar
        open={failedSnack}
        color="danger"
        autoHideDuration={6000}
        onClose={() => setFailedSnack(false)}
      >
        {errorMessage}
      </Snackbar>
    </ThemeProvider>
  );
}
